/**
 * How an admin user row is DESCRIBED — separated from the table that draws it so the rules are
 * testable without a DOM.
 *
 * ⭐⭐ These are the judgements a reader will act on ("is this account able to log in?"), and every
 *   one of them combines more than one field. That is exactly the kind of logic that quietly goes
 *   wrong inside JSX, where it is only ever verified by looking at a screenshot of the happy case.
 */
import type { AdminUser } from '../../api/types';

/**
 * ⭐ NAMES THE PRINCIPAL, not the credential. `user_type` and `auth_method` are shown as separate
 *   columns; this is the one-word answer to "what am I looking at", and it deliberately reads
 *   "Agent" for a service account rather than "Service", because the reader's question is about the
 *   THING, not about how it signs in.
 */
export function principalLabel(user: AdminUser): string {
  return user.user_type === 'ai_agent' ? 'Agent' : 'Person';
}

export const AUTH_METHOD_LABEL: Record<AdminUser['auth_method'], string> = {
  gitlab: 'GitLab',
  local: 'Password + 2FA',
  service: 'Service token',
};

export type AccessState =
  | { kind: 'ok'; label: string }
  | { kind: 'blocked'; label: string; detail: string }
  | { kind: 'attention'; label: string; detail: string };

/**
 * 🔴 THE COLUMN THAT MATTERS, and the one an admin cannot compute in their head.
 *
 * ⭐⭐ "Enrolment pending" is the state this whole feature turns on and the reason it gets its own
 *   visible category rather than a blank cell: a local account with no verified TOTP EXISTS, has a
 *   working password, and CANNOT HOLD A SESSION — the database refuses the row. Without a label for
 *   it, that account is indistinguishable from a healthy one until the person complains they cannot
 *   log in, and the admin has no way to see why.
 *
 * ⚠️ Note the ORDER of the checks. Deactivated wins over enrolment: an inactive account's TOTP state
 *   is not the reason it cannot log in, and reporting the subordinate cause would send the admin to
 *   fix the wrong thing.
 */
export function accessState(user: AdminUser): AccessState {
  if (!user.is_active) {
    return {
      kind: 'blocked',
      label: 'Deactivated',
      detail: 'Cannot sign in. Existing sessions were revoked.',
    };
  }

  if (user.auth_method === 'local' && !user.totp_enrolled) {
    return {
      kind: 'attention',
      label: 'Enrolment pending',
      detail:
        'The account exists and the password works, but no authenticator is enrolled — so it cannot hold a session yet. The user completes this at their next sign-in.',
    };
  }

  return { kind: 'ok', label: 'Active' };
}

/**
 * ⛔ Which admin actions APPLY to this row. A control that would always 404 or 409 should not be
 *   drawn — an admin clicking "Force 2FA re-enrolment" on a GitLab account and getting a refusal
 *   learns nothing except that the screen offers things it cannot do.
 */
export function availableActions(user: AdminUser, currentUserId: string) {
  const isSelf = user.id === currentUserId;
  const isLocal = user.auth_method === 'local';

  return {
    /** ⭐ Only local accounts have a password or a second factor to reset. */
    canResetPassword: isLocal && user.is_active,
    canForceReenrol: isLocal && user.totp_enrolled,
    canRevokeSessions: Number(user.active_sessions) > 0,
    /**
     * ⚠️ NOT hidden for the last admin — the API owns that rule (db/005's trigger) and answers 409
     * with a reason. This client cannot know whether another active admin exists without asking, and
     * a guess here would either hide a legal action or offer an illegal one.
     * ⭐ Self is different: it is knowable from data already on screen, and an admin removing their
     * own access by misclick is worth preventing rather than explaining.
     */
    canChangeAdmin: !isSelf,
    canChangeActive: !isSelf,
    isSelf,
  };
}
